// Networks view: a clean, clickable list of networks with full CRUD. Creating a
// network lives behind a "+ New network" MODAL (the creator becomes owner); a row
// click opens a right-side DETAIL Drawer with the network's members and Edit
// (PATCH) / Delete (DELETE, confirm) actions. A non-owner edit/delete 403 surfaces
// a friendly message rather than crashing.

import { useState } from 'react';
import { api, ApiError } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { useResource } from '../lib/useResource';
import { Card, Loading, ErrorState, Badge, StatusBadge } from '../components/ui';
import { DataTable, type Column } from '../components/DataTable';
import { Modal, ConfirmDialog } from '../components/Modal';
import { Drawer, DetailRow } from '../components/Drawer';
import type { Network, NetworkMembership } from '../api/schemas';

type Vis = Network['visibility'];
type Dur = Network['durability_policy'];
type MemberRole = NetworkMembership['role'];

export function Networks() {
  const nets = useResource(() => api.networks(), []);
  const [creating, setCreating] = useState(false);
  const [selected, setSelected] = useState<Network | null>(null);

  const cols: Column<Network>[] = [
    { key: 'name', header: 'Name', cell: (n) => <b>{n.name}</b> },
    { key: 'slug', header: 'Slug', cell: (n) => <code className="mono small">/{n.slug}</code> },
    { key: 'vis', header: 'Visibility', cell: (n) => <Badge tone="muted">{n.visibility}</Badge> },
    { key: 'dur', header: 'Durability', cell: (n) => <span className="small">{n.durability_policy}</span> },
    { key: 'members', header: 'Members', align: 'right', cell: (n) => n.member_count ?? '—' },
    { key: 'status', header: 'Status', cell: (n) => <StatusBadge status={n.status} /> },
  ];

  return (
    <div className="view">
      <div className="view-head">
        <h1>Networks</h1>
        <div className="view-head-actions">
          <button className="btn btn-primary" onClick={() => setCreating(true)}>+ New network</button>
        </div>
      </div>

      <Card title="Networks">
        {nets.loading ? (
          <Loading what="Loading networks…" />
        ) : nets.error || !nets.data ? (
          <ErrorState message={nets.error ?? 'no data'} onRetry={nets.reload} />
        ) : (
          <DataTable
            columns={cols}
            rows={nets.data.items}
            rowKey={(n) => n.id}
            empty="No networks — create one."
            onRowClick={(n) => setSelected(n)}
          />
        )}
      </Card>

      {/* UX9 — "Network memberships" used to be a card on Profile, where it was a
          read-only fact on a page about who you are. A membership is a fact about a
          NETWORK, so it lives with the networks, right under the list it indexes into. */}
      <MyMemberships names={new Map((nets.data?.items ?? []).map((n) => [n.id, n.name]))} />

      {creating && (
        <NetworkModal
          title="New network"
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false);
            nets.reload();
          }}
        />
      )}

      {selected && (
        <NetworkDrawer
          network={selected}
          onClose={() => setSelected(null)}
          onChanged={(next) => {
            nets.reload();
            setSelected(next);
          }}
        />
      )}
    </div>
  );
}

/** The signed-in caller's own memberships, resolved to network names (UX9).
 *
 * Sourced from /auth/me, which every session already holds — no extra request, and it
 * is the one list the gateway guarantees is about THIS caller.
 *
 * It renders EMPTY rather than hiding: "you are a member of none of these" is an
 * answer, and a card that appears only for some accounts is a card nobody learns is
 * there. This is what the Profile copy did before the move, too. */
function MyMemberships({ names }: { names: Map<string, string> }) {
  const { me } = useAuth();
  const memberships = me?.memberships ?? [];
  return (
    <Card title="My network memberships">
      {memberships.length === 0 ? (
        <div className="muted small" data-testid="my-memberships">
          You are not a member of any network. The list above is what you can see; a
          membership is what lets you publish into one.
        </div>
      ) : (
        <ul className="feed" data-testid="my-memberships">
          {memberships.map((m) => (
            <li key={m.network_id} className="feed-row">
              <Badge tone={m.role === 'owner' ? 'info' : 'muted'}>{m.role}</Badge>
              <span className="small">{names.get(m.network_id) ?? m.network_id}</span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function NetworkDrawer({
  network,
  onClose,
  onChanged,
}: {
  network: Network;
  onClose: () => void;
  onChanged: (next: Network | null) => void;
}) {
  const members = useResource(() => api.networkMembers(network.id), [network.id]);
  // Org users for the "add member" picker. This endpoint is admin-only; a non-admin
  // 403 just means the picker is hidden (members still render + can be removed).
  const users = useResource(() => api.users(), []);
  // Editing and deleting a network belong to its owner (or an org admin) — the gateway
  // refuses both from a plain member with a 403 (deleting a network you were merely added
  // to used to be possible; it is not any more). Show only what the caller may actually do:
  // a button that always errors is a worse answer than no button.
  const { user, isAdmin } = useAuth();
  const mayAdminister = mayAdministerNetwork(network.owner_id, user?.id, isAdmin);
  const [editing, setEditing] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [addUserId, setAddUserId] = useState('');
  const [addRole, setAddRole] = useState<MemberRole>('member');
  const [memBusy, setMemBusy] = useState(false);
  const [memErr, setMemErr] = useState<string | null>(null);

  async function addMember() {
    if (!addUserId) return;
    setMemBusy(true);
    setMemErr(null);
    try {
      await api.addNetworkMember(network.id, { user_id: addUserId, role: addRole });
      setAddUserId('');
      members.reload();
    } catch (e) {
      setMemErr(friendly(e));
    } finally {
      setMemBusy(false);
    }
  }

  async function removeMember(userId: string) {
    setMemBusy(true);
    setMemErr(null);
    try {
      await api.removeNetworkMember(network.id, userId);
      members.reload();
    } catch (e) {
      setMemErr(friendly(e));
    } finally {
      setMemBusy(false);
    }
  }

  async function del() {
    setBusy(true);
    setErr(null);
    try {
      await api.deleteNetwork(network.id);
      onChanged(null);
      onClose();
    } catch (e) {
      setErr(friendly(e));
      setBusy(false);
    }
  }

  return (
    <Drawer
      title={network.name}
      subtitle={<code className="mono small">/{network.slug}</code>}
      onClose={onClose}
      actions={
        mayAdminister ? (
          <>
            <button className="btn btn-sm" onClick={() => setEditing(true)}>Edit</button>
            <button className="btn btn-sm btn-danger" data-testid="network-delete" onClick={() => setConfirmDel(true)}>Delete</button>
          </>
        ) : null
      }
    >
      {err && <ErrorState message={err} />}
      <DetailRow label="Visibility"><Badge tone="muted">{network.visibility}</Badge></DetailRow>
      <DetailRow label="Durability">{network.durability_policy}</DetailRow>
      <DetailRow label="Status"><StatusBadge status={network.status} /></DetailRow>
      <DetailRow label="Owner"><code className="mono small">{network.owner_id}</code></DetailRow>
      {network.global_tier_instance && (
        <DetailRow label="Global tier"><code className="mono small">{network.global_tier_instance}</code></DetailRow>
      )}
      {network.description && <DetailRow label="Description">{network.description}</DetailRow>}

      <div>
        <h4>Members</h4>
        {members.loading ? (
          <Loading what="Loading members…" />
        ) : members.error || !members.data ? (
          <ErrorState message={members.error ?? 'no data'} />
        ) : members.data.items.length === 0 ? (
          <div className="muted small">No members.</div>
        ) : (
          <ul className="feed">
            {members.data.items.map((m) => (
              <li key={m.user_id} className="feed-row">
                <Badge tone={m.role === 'owner' ? 'info' : 'muted'}>{m.role}</Badge>
                <span className="small">{m.user_email ?? m.user_id}</span>
                <button
                  className="btn btn-sm btn-danger"
                  disabled={memBusy}
                  onClick={() => removeMember(m.user_id)}
                  aria-label={`Remove ${m.user_email ?? m.user_id}`}
                  title="Remove member"
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
        )}

        {/* Add-member control (admin-only users list; hidden if it 403s). */}
        {users.data && (
          <div className="add-member" style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <select value={addUserId} onChange={(e) => setAddUserId(e.target.value)} disabled={memBusy}>
              <option value="">Add member…</option>
              {users.data.items
                .filter((u) => !(members.data?.items ?? []).some((m) => m.user_id === u.id))
                .map((u) => (
                  <option key={u.id} value={u.id}>{u.name ? `${u.name} (${u.email})` : u.email}</option>
                ))}
            </select>
            <select value={addRole} onChange={(e) => setAddRole(e.target.value as MemberRole)} disabled={memBusy}>
              <option value="member">member</option>
              <option value="admin">admin</option>
              <option value="owner">owner</option>
            </select>
            <button className="btn btn-sm btn-primary" disabled={!addUserId || memBusy} onClick={addMember}>
              {memBusy ? 'Adding…' : 'Add'}
            </button>
          </div>
        )}
        {memErr && <div className="form-error">{memErr}</div>}
      </div>

      {editing && (
        <NetworkModal
          title="Edit network"
          network={network}
          onClose={() => setEditing(false)}
          onSaved={(next) => {
            setEditing(false);
            onChanged(next ?? network);
          }}
        />
      )}
      {confirmDel && (
        <ConfirmDialog
          title="Delete network"
          message={<>Delete <b>{network.name}</b>? This removes the network and its memberships.</>}
          confirmLabel="Delete network"
          busy={busy}
          error={err}
          onConfirm={del}
          onClose={() => setConfirmDel(false)}
        />
      )}
    </Drawer>
  );
}

// Shared create/edit modal. With `network` it PATCHes (slug immutable); without it
// POSTs a new network. Returns the saved Network to the caller.
function NetworkModal({
  title,
  network,
  onClose,
  onSaved,
}: {
  title: string;
  network?: Network;
  onClose: () => void;
  onSaved: (next?: Network) => void;
}) {
  const editing = !!network;
  const [slug, setSlug] = useState(network?.slug ?? '');
  const [name, setName] = useState(network?.name ?? '');
  const [description, setDescription] = useState(network?.description ?? '');
  const [visibility, setVisibility] = useState<Vis>(network?.visibility ?? 'private');
  const [durability, setDurability] = useState<Dur>(network?.durability_policy ?? 'local-first');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      let saved: Network;
      if (editing) {
        saved = await api.patchNetwork(network!.id, {
          name: name.trim(),
          description: description.trim() || null,
          visibility,
          durability_policy: durability,
        });
      } else {
        saved = await api.createNetwork({
          slug: slug.trim(),
          name: name.trim(),
          description: description.trim() || null,
          visibility,
          durability_policy: durability,
        });
      }
      onSaved(saved);
    } catch (ex) {
      setErr(friendly(ex));
      setBusy(false);
    }
  }

  return (
    <Modal
      title={title}
      onClose={onClose}
      footer={
        <>
          <button className="btn btn-sm" disabled={busy} onClick={onClose}>Cancel</button>
          <button
            className="btn btn-sm btn-primary"
            disabled={busy || !name.trim() || (!editing && !slug.trim())}
            onClick={submit}
          >
            {busy ? 'Saving…' : editing ? 'Save changes' : 'Create network'}
          </button>
        </>
      }
    >
      <form onSubmit={submit} className="issue-form">
        {!editing && (
          <label className="field">Slug
            <input value={slug} onChange={(e) => setSlug(e.target.value)} placeholder="lowercase-no-spaces (a–z, 0–9, -)" required />
          </label>
        )}
        <label className="field">Name
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="My Network" required />
        </label>
        <label className="field">Description
          <input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="optional" />
        </label>
        <label className="field">Visibility
          <select value={visibility} onChange={(e) => setVisibility(e.target.value as Vis)}>
            <option value="private">private</option>
            <option value="org">org</option>
            <option value="public">public</option>
          </select>
        </label>
        <label className="field">Durability
          <select value={durability} onChange={(e) => setDurability(e.target.value as Dur)}>
            <option value="local-first">local-first</option>
            <option value="always-global">always-global</option>
          </select>
        </label>
        {err && <div className="form-error">{err}</div>}
      </form>
    </Modal>
  );
}

/**
 * Who may edit or delete a network: its owner, or an org admin. Pure (exported for tests).
 *
 * This mirrors the gateway's own guard rather than guessing at it — `DELETE /networks/{id}`
 * and the visibility field of `PATCH` both resolve through `is_network_admin`. A member who
 * merely belongs to the network gets 403 from the server, so the buttons are not drawn for
 * them; an unknown caller id (no session yet) is never an administrator.
 */
export function mayAdministerNetwork(ownerId: string | undefined, userId: string | undefined, isAdmin: boolean): boolean {
  if (isAdmin) return true;
  return !!ownerId && !!userId && ownerId === userId;
}

// Map an error to a friendly message — a 403 means the caller isn't the owner.
export function friendly(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.status === 403) return "You don't have permission for this action (owner/admin only).";
    return e.message;
  }
  return String(e);
}
